import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ShieldIcon from '@mui/icons-material/Shield';
import PersonIcon from '@mui/icons-material/Person';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import DashboardLayout from '../layouts/DashboardLayout';
import UserFormDialog from '../components/UserFormDialog';
import usersApi, { deleteUser, getCalendarToken, listUsers } from '../api/users';
import { listSubjects } from '../api/subjects';
import { formatDateTime } from '../utils/format';
import { useAuth } from '../context/AuthContext';

const roleChips = {
  superadmin: { label: 'Super Admin', color: 'error' },
  admin: { label: 'Admin', color: 'primary' },
  educator: { label: 'Educator', color: 'success' },
  student: { label: 'Student', color: 'info' },
};

const roleFilters = [
  { value: 'all', label: 'All roles' },
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'educator', label: 'Educator' },
  { value: 'student', label: 'Student' },
];

const UsersPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarFeedUrl, setCalendarFeedUrl] = useState('');
  const [calendarStudent, setCalendarStudent] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [subjectMap, setSubjectMap] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useAuth();

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = roleFilter === 'all' ? {} : { role: roleFilter };
      const data = await listUsers(params);
      setRows(data);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to load users', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, roleFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadSubjects = useCallback(async () => {
    try {
      const data = await listSubjects();
      const list = Array.isArray(data) ? data : [];
      const nextMap = {};
      list.forEach((subject) => {
        if (subject?._id) nextMap[subject._id] = subject.name || subject._id;
      });
      setSubjectMap(nextMap);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to load subjects', { variant: 'warning' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadSubjects();
  }, [loadSubjects]);

  const handleCreate = () => {
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const handleEdit = (row) => {
    setSelectedUser(row);
    setDialogOpen(true);
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(`Delete ${row.name} ${row.surname} This cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteUser(row._id);
      enqueueSnackbar('User deleted', { variant: 'success' });
      loadUsers();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to delete user', { variant: 'error' });
    }
  };

  const handleDialogSubmit = async (payload) => {
    try {
      setDialogLoading(true);
      if (selectedUser?._id) {
        await usersApi.updateUser(selectedUser._id, payload);
        enqueueSnackbar('User updated', { variant: 'success' });
      } else {
        await usersApi.createUser(payload);
        enqueueSnackbar('User created', { variant: 'success' });
      }
      setDialogOpen(false);
      loadUsers();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to save user', { variant: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCalendarFeed = async (row) => {
    if (!row?._id) return;
    try {
      const data = await getCalendarToken(row._id);
      setCalendarFeedUrl(data?.feedUrl || '');
      setCalendarStudent(row);
      setCalendarDialogOpen(true);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to load calendar feed', { variant: 'error' });
    }
  };

  const handleCopyFeed = async () => {
    try {
      await navigator.clipboard.writeText(calendarFeedUrl);
      enqueueSnackbar('Calendar feed copied', { variant: 'success' });
    } catch {
      enqueueSnackbar('Copy failed. Select and copy the URL manually.', { variant: 'warning' });
    }
  };

  const columns = useMemo(() => [
    {
      field: 'fullName',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const row = params?.row || {};
        const name = `${row.name || ''} ${row.surname || ''}`.trim() || '-';
        return (
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {name}
          </Typography>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {params?.row?.email || '-'}
        </Typography>
      ),
    },
    {
      field: 'role',
      headerName: 'Role',
      minWidth: 130,
      renderCell: (params) => {
        const roleVal = params?.row?.role || 'unknown';
        const config = roleChips[roleVal] || { label: roleVal, color: 'default' };
        return <Chip size="small" color={config.color} label={config.label} />;
      },
    },
    {
      field: 'grade',
      headerName: 'Grade',
      minWidth: 100,
      valueGetter: (params) => {
        const row = params?.row || {};
        if (row.role === 'student') return row.grade ?? 'N/A';
        return 'N/A';
      },
    },
    {
      field: 'subjects',
      headerName: 'Subjects',
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => {
        const subs = params?.row?.subjects;
        if (Array.isArray(subs)) {
          return subs
            .map((item) => {
              if (!item) return null;
              if (typeof item === 'string') return subjectMap[item] || item;
              if (typeof item === 'object') {
                return item.name || subjectMap[item._id] || item._id || item.code || null;
              }
              return String(item);
            })
            .filter(Boolean)
            .join(', ') || '-';
        }
        if (typeof subs === 'string') return subs;
        return '-';
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      minWidth: 190,
      valueGetter: (params) => (params?.row?.createdAt ? formatDateTime(params.row.createdAt) : '-'),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params?.row;
        return (
          <Stack direction="row" spacing={1}>
            {row?.role === 'student' && (
              <Tooltip title="Calendar feed">
                <IconButton size="small" color="primary" onClick={() => handleCalendarFeed(row)}>
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" onClick={() => row && handleEdit(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => row && handleDelete(row)}
                  disabled={!row || row._id === currentUser?._id}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ], [currentUser?._id, handleDelete, handleEdit, subjectMap]);

  return (
    <DashboardLayout
      title="User Management"
      actions={(
        <Stack direction="row" spacing={1}>
          <TextField
            select
            size="small"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            sx={{ minWidth: 160 }}
          >
            {roleFilters.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Refresh">
            <IconButton onClick={loadUsers}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleCreate}>
            Add User
          </Button>
        </Stack>
      )}
    >
      <Card elevation={1} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                Directory
              </Typography>
              <Chip icon={<ShieldIcon />} label="Admins" variant="outlined" />
              <Chip icon={<PersonIcon />} label="Educators & Students" variant="outlined" color="primary" />
            </Stack>
            <Box sx={{ height: 520 }}>
              <DataGrid
                rows={rows}
                getRowId={(row) => row._id}
                columns={columns}
                loading={loading}
                disableRowSelectionOnClick
                sx={{
                  border: 'none',
                  color: 'text.primary',
                  bgcolor: 'background.paper',
                  '& .MuiDataGrid-virtualScroller': {
                    bgcolor: 'background.paper',
                  },
                  '& .MuiDataGrid-row': {
                    bgcolor: 'background.paper',
                  },
                  '& .MuiDataGrid-cell': {
                    color: 'text.primary',
                  },
                  '& .MuiDataGrid-cellContent': {
                    color: 'text.primary',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    bgcolor: 'grey.50',
                    color: 'text.primary',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 700,
                    color: 'text.primary',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    bgcolor: 'grey.50',
                    color: 'text.primary',
                  },
                  '& .MuiDataGrid-overlay': {
                    color: 'text.secondary',
                  },
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25, 50]}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <UserFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialValues={selectedUser}
        onSubmit={handleDialogSubmit}
        loading={dialogLoading}
      />
      <Dialog open={calendarDialogOpen} onClose={() => setCalendarDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Calendar feed</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Share this URL with the learner/parent to subscribe in Google/Apple/Outlook calendars.
            </Typography>
            <Typography variant="subtitle2">
 {calendarStudent ? `${calendarStudent.name || ''} ${calendarStudent.surname || ''}`.trim() : 'Student'}
            </Typography>
            <TextField
              value={calendarFeedUrl}
              fullWidth
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarDialogOpen(false)} color="inherit">
            Close
          </Button>
          <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyFeed} disabled={!calendarFeedUrl}>
            Copy link
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default UsersPage;
